import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Package, ChevronDown, Plus, Minus } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { message, Select, Spin } from "antd";
import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";
import { foodApiService, BarcodeMealPayload } from "../../services/foodApiService";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import PredictedGlucoseSpikeCard from "../../components/HomePage/PredictedGlucoseSpikeCard";
import MedicalDisclaimerNotice from "../../components/MedicalDisclaimerNotice";
import NutritionFactsCard from "../../components/common/NutritionFactsCard";
import { MEAL_DETAIL_MACRO_CLASS } from "../../utils/nutritionMacroColors";

const { Option } = Select;

interface ScannedFoodData {
  name: string;
  brand?: string;
  quantity?: string;
  image?: string;
  calories?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  sugar?: number;
  fiber?: number;
  saturated_fat?: number;
  trans_fat?: number;
  sodium?: number;
  cholesterol?: number;
  alcohol?: number;
  caffeine?: number;
  gi_estimate?: number;
  gl_estimate?: number;
  net_carbs?: number;
  barcode?: string;
  meal_type?: string;
}

const MEAL_TYPE_OPTIONS = ["Breakfast", "Lunch", "Dinner", "Snack"] as const;

function normalizeMealTypeOption(value?: string | null): string {
  if (!value?.trim()) return "";
  const trimmed = value.trim();
  const match = MEAL_TYPE_OPTIONS.find(
    (option) => option.toLowerCase() === trimmed.toLowerCase()
  );
  return match ?? trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

function resolveMealTypeFromSources(
  ...sources: Array<string | null | undefined>
): string {
  for (const source of sources) {
    const normalized = normalizeMealTypeOption(source);
    if (normalized) return normalized;
  }
  return "";
}

// Round to 1 decimal if value has more than 1 decimal place
const fmt = (value: number): string => {
  const str = value.toString();
  const decimalIndex = str.indexOf(".");
  if (decimalIndex !== -1 && str.length - decimalIndex - 1 > 1) {
    return parseFloat(value.toFixed(1)).toString();
  }
  return str;
};

// Helper to parse quantity number from string like "230g" or "100ml"
const parseQuantityNumber = (qty: string): number => {
  const match = qty.match(/(\d+\.?\d*)/);
  return match ? parseFloat(match[1]) : 100;
};

const getQuantityUnit = (qty: string): string => {
  const match = qty.match(/[a-zA-Z]+/);
  return match ? match[0] : "g";
};

const formatQuantityLabel = (num: number, unit: string): string => {
  const normalized = unit.toLowerCase();
  if (normalized === "serving" || normalized === "servings") {
    return `${num} ${num === 1 ? "serving" : "servings"}`;
  }
  if (/^[a-z]+$/i.test(unit)) {
    return `${num} ${unit}`;
  }
  return `${num}${unit}`;
};

const ScannedFoodDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [foodData, setFoodData] = useState<ScannedFoodData | null>(null);
  const [selectedMealType, setSelectedMealType] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [servingCount, setServingCount] = useState(1);
  const [analyzeApiData, setAnalyzeApiData] = useState<any>(null);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);

  // Prefer freshly fetched API data, fallback to location.state
  const glucoseSpikeData =
    analyzeApiData?.glucose_spike ??
    location.state?.glucoseSpikeData ??
    location.state?.glucose_spike ??
    null;

  // Memoized multiply helper — only recalculates when servingCount changes
  const multiply = useCallback(
    (value: number) => Math.round(value * servingCount * 10) / 10,
    [servingCount]
  );

  // Memoized computed nutrition values — recalculates only when foodData or servingCount changes
  const nutrition = useMemo(() => {
    if (!foodData) return null;
    return {
      calories: multiply(foodData.calories || 0),
      protein: multiply(foodData.protein || 0),
      carbs: multiply(foodData.carbs || 0),
      fat: multiply(foodData.fat || 0),
      sugar: multiply(foodData.sugar || 0),
      fiber: multiply(foodData.fiber || 0),
      saturated_fat: multiply(foodData.saturated_fat || 0),
      trans_fat: multiply(foodData.trans_fat || 0),
      sodium: multiply(foodData.sodium || 0),
      cholesterol: multiply(foodData.cholesterol || 0),
      alcohol: multiply(foodData.alcohol || 0),
      caffeine: multiply(foodData.caffeine || 0),
    };
  }, [foodData, multiply]);

  const nutritionTotalsBarcode = useMemo(() => {
    if (!nutrition || !foodData) return null;
    const netCarbs =
      foodData.net_carbs != null
        ? multiply(foodData.net_carbs)
        : Math.max(0, nutrition.carbs - nutrition.fiber);
    return {
      carbohydrate_content_g: nutrition.carbs,
      dietary_fiber_g: nutrition.fiber,
      net_carbs_g: netCarbs,
      sugar_g: nutrition.sugar,
      protein_content_g: nutrition.protein,
      fat_content_g: nutrition.fat,
      trans_fat_g: nutrition.trans_fat,
      saturated_fat_g: nutrition.saturated_fat,
      calorie_content_kcal: nutrition.calories,
      GI_estimate: Math.round(foodData.gi_estimate ?? 0),
      GL_estimate: foodData.gl_estimate ?? 0,
    };
  }, [nutrition, foodData, multiply]);

  // Memoized quantity display
  const quantityDisplay = useMemo(() => {
    const rawQty = foodData?.quantity?.trim();
    if (rawQty) {
      const num = parseQuantityNumber(rawQty) * servingCount;
      const unit = getQuantityUnit(rawQty);
      const text = formatQuantityLabel(num, unit);
      return { text, raw: text };
    }
    const text = formatQuantityLabel(servingCount, "serving");
    return { text, raw: text };
  }, [foodData, servingCount]);

  useEffect(() => {
    // Get food data from location state (passed from barcode scan)
    const data = location.state?.foodData as ScannedFoodData;

    if (!data) {
      message.error("No product data found. Please scan a barcode.");
      navigate(-1);
      return;
    }

    setFoodData(data);
    setSelectedMealType((prev) =>
      prev ||
      resolveMealTypeFromSources(
        data.meal_type,
        location.state?.meal_type as string | undefined
      )
    );
    setLoading(false);

    // If a document_id is available, call analyze_food_image API for fresh glucose data
    const documentId = location.state?.document_id ?? location.state?.documentId ?? null;
    if (documentId) {
      setAnalyzeLoading(true);
      foodApiService
        .predictFood(documentId)
        .then((resp: any) => {
          const raw = resp?.data?._rawResponse ?? resp?.data?.data ?? resp?.data ?? null;
          if (raw) {
            setAnalyzeApiData(raw);
          }
        })
        .catch(() => {
          // silently ignore — fall back to location.state data
        })
        .finally(() => setAnalyzeLoading(false));
    }
  }, [location, navigate]);

  useEffect(() => {
    const mealType = resolveMealTypeFromSources(
      analyzeApiData?.meal_type,
      analyzeApiData?.other_details?.meal_type
    );
    if (mealType) {
      setSelectedMealType(mealType);
    }
  }, [analyzeApiData]);

  const handleAddToMeal = async () => {
    if (!selectedMealType) {
      message.warning("Please select a meal type");
      return;
    }

    if (!foodData) {
      message.error("No food data available");
      return;
    }

    setIsSaving(true);
    const loadingMsg = message.loading("Saving to meal...", 0);

    try {
      // Generate current date in UTC format
      const currentDate = new Date();
      const year = currentDate.getUTCFullYear();
      const month = String(currentDate.getUTCMonth() + 1).padStart(2, "0");
      const day = String(currentDate.getUTCDate()).padStart(2, "0");
      const hours = String(currentDate.getUTCHours()).padStart(2, "0");
      const minutes = String(currentDate.getUTCMinutes()).padStart(2, "0");
      const seconds = String(currentDate.getUTCSeconds()).padStart(2, "0");
      const dateString = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

      // Create barcode meal payload (flat format matching curl)
      const barcodePayload: BarcodeMealPayload = {
        success: {
          code: "Retrieved",
          message: "",
        },
        barcode: foodData.barcode || "",
        date: dateString,
        product_name: foodData.name,
        generic_name: "",
        quantity: quantityDisplay.raw,
        brand: foodData.brand || "",
        calories: nutrition!.calories,
        protein: nutrition!.protein,
        carbohydrates: nutrition!.carbs,
        sugar: nutrition!.sugar,
        fat: nutrition!.fat,
        saturated_fat: nutrition!.saturated_fat,
        trans_fat: nutrition!.trans_fat,
        fiber: nutrition!.fiber,
        sodium: nutrition!.sodium,
        cholesterol: nutrition!.cholesterol,
        alcohol: nutrition!.alcohol,
        caffeine: nutrition!.caffeine,
        meal_type: selectedMealType,
      };

      // Call API to save meal
      const response = await foodApiService.saveMeal(barcodePayload);

      loadingMsg();

      await queryClient.invalidateQueries({ queryKey: ["homePageData"] });
      await queryClient.invalidateQueries({ queryKey: ["home-active-meal-detail"] });
      await queryClient.invalidateQueries({ queryKey: ["meal-action-window"] });
      await queryClient.invalidateQueries({ queryKey: ["intervention-calculate"] });

      // Navigate to Meal page to show the saved meal
      setTimeout(() => {
        navigate("/glucera" + GluceraNavigationRoutes.Mealpage);
      }, 500);
    } catch (error: any) {
      loadingMsg();
      console.error("Error saving barcode meal:", error.message, error.response?.data);

      // Determine error type and show appropriate message
      let errorMsg = "Unable to save meal data. ";

      if (error.response) {
        // Server responded with error
        const status = error.response.status;
        const data = error.response.data;

        if (status === 400) {
          errorMsg += data?.message || data?.error || data?.non_field_errors?.[0] || "Invalid meal data. Please check all fields.";
        } else if (status === 401 || status === 403) {
          errorMsg += "Authentication failed. Please log in again.";
        } else if (status === 404) {
          errorMsg += "Meal service not found. Please contact support.";
        } else if (status === 500) {
          errorMsg += "Server error. Please try again later.";
        } else {
          errorMsg += data?.message || data?.error || `Server error (${status})`;
        }
      } else if (error.request) {
        // Request made but no response
        errorMsg += "No response from server. Please check your internet connection.";
      } else {
        // Something else happened
        errorMsg += error.message || "An unexpected error occurred.";
      }

      message.error({
        content: errorMsg,
        duration: 5,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-subhead text-muted-foreground">Loading product...</p>
        </div>
      </div>
    );
  }

  const food = foodData!;

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-24"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10 rounded-xl border-border bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-bold text-foreground">Product Info</h1>
          <div className="h-10 w-10" />
        </div>

        {/* Product Info Card with Image */}
        <Card className="rounded-2xl border-0 bg-card p-5 shadow-sm">
          <div className="flex items-start gap-3">
            {/* Product Image — set by barcodeApiService from Open Food Facts */}
            {food.image && !imageError ? (
              <img
                src={food.image}
                alt={food.name}
                className="h-14 w-14 flex-shrink-0 rounded-xl object-cover border border-border"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/30">
                <Package className="h-7 w-7 text-blue-600 dark:text-blue-400" />
              </div>
            )}

            <div className="min-w-0 flex-1">
              <h2 className="break-words text-title3 font-bold leading-snug text-foreground">
                {food.name}
              </h2>
              {food.brand ? (
                <p className="mt-0.5 break-words text-subhead text-muted-foreground">
                  {food.brand}
                </p>
              ) : null}
              {food.quantity?.trim() ? (
                <span className="mt-2 inline-flex rounded-full bg-green-500 px-3 py-1 text-caption1 font-semibold text-white">
                  {formatQuantityLabel(parseQuantityNumber(food.quantity), getQuantityUnit(food.quantity))}
                </span>
              ) : null}
            </div>
          </div>
        </Card>

        {/* Serving Count Multiplier */}
        <Card className="rounded-2xl border-0 bg-card p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-subhead font-semibold text-foreground">Serving</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setServingCount(prev => Math.max(1, prev - 1))}
                disabled={servingCount <= 1}
                className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-all active:scale-95 ${servingCount <= 1
                  ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                  : 'border-blue-400 text-blue-500 hover:bg-blue-50'
                  }`}
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="min-w-[40px] text-center text-title2 font-bold text-foreground">
                {servingCount}
              </span>
              <button
                onClick={() => setServingCount(prev => prev + 1)}
                className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-blue-400 text-blue-500 hover:bg-blue-50 transition-all active:scale-95"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Card>


        {nutritionTotalsBarcode && (
          <NutritionFactsCard
            nutritionTotals={nutritionTotalsBarcode}
            foodItems={[{ id: 0, weight: 1 }]}
            headerMeta={`per ${quantityDisplay.text}`}
            bottomSlot={
              <div>
                <h3 className="text-headline mb-3 font-bold text-foreground">Other</h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <div>
                    <p className="text-subhead mb-1 text-muted-foreground">Sodium</p>
                    <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.sodium}`}>
                      {fmt(nutrition?.sodium ?? 0)}g
                    </p>
                  </div>
                  <div>
                    <p className="text-subhead mb-1 text-muted-foreground">Cholesterol</p>
                    <p className={`text-title2 font-bold ${MEAL_DETAIL_MACRO_CLASS.cholesterol}`}>
                      {fmt(nutrition?.cholesterol ?? 0)}g
                    </p>
                  </div>
                </div>
              </div>
            }
          />
        )}

        {/* Predicted Glucose Spike Card */}
        {nutrition && glucoseSpikeData && (
          <>
            <PredictedGlucoseSpikeCard
              glucoseSpikeData={glucoseSpikeData}
              spikeValue={glucoseSpikeData?.predicted_spike_mg_dl}
              mealsLogged={1}
              variant="curve"
              showPredictedRange={true}
            />
            <MedicalDisclaimerNotice />
          </>
        )}

        {/* Add to Meal Section */}
        <Card className="rounded-2xl border-0 bg-card p-5 shadow-sm">
          <h3 className="text-subhead font-semibold text-foreground mb-3">Add to</h3>

          <Select
            placeholder="Select meal type"
            className="w-full"
            size="large"
            value={selectedMealType || undefined}
            onChange={(value) => setSelectedMealType(value)}
            suffixIcon={<ChevronDown className="h-4 w-4 text-muted-foreground" />}
          >
            <Option value="Breakfast">Breakfast</Option>
            <Option value="Lunch">Lunch</Option>
            <Option value="Dinner">Dinner</Option>
            <Option value="Snack">Snack</Option>
          </Select>
        </Card>

        {/* Add to Meal Button */}
        <Button
          onClick={handleAddToMeal}
          className="w-full rounded-2xl bg-blue-400 py-6 text-callout font-semibold text-white hover:bg-blue-500"
          disabled={!selectedMealType || isSaving}
        >
          {isSaving ? "Saving..." : "+ Add to Meal"}
        </Button>
      </div>

      {/* Bottom Navigation */}
      <BottomNavBar />
    </div>
  );
};

export default ScannedFoodDetail;
